import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Group,
  SimpleGrid,
  Card,
  Stack,
  ThemeIcon,
  RingProgress,
  Center,
  Button,
  Badge,
  Grid,
  Alert,
} from "@mantine/core";
import {
  IconPackage,
  IconBuildingStore,
  IconBrain,
  IconTicket,
  IconChartBar,
  IconBell,
  IconUsers,
  IconMovie,
  IconShirt,
  IconSpeakerphone,
  IconDeviceMobile,
  IconCheck,
  IconArrowRight,
  IconInfoCircle,
  IconAlertTriangle,
} from "@tabler/icons-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  Cell,
} from "recharts";
import { API_BASE_URL } from "../../config/baseUrls";

const weeklyPulseData = [
  { day: "Mon", DAU: 4000, Engagement: 2400 },
  { day: "Tue", DAU: 4200, Engagement: 2600 },
  { day: "Wed", DAU: 3800, Engagement: 2200 },
  { day: "Thu", DAU: 5100, Engagement: 3500 },
  { day: "Fri", DAU: 6800, Engagement: 4800 },
  { day: "Sat", DAU: 8500, Engagement: 6200 },
  { day: "Sun", DAU: 7900, Engagement: 5900 },
];

// Sample data illustrating the 3 approved B2B buyer-persona deliverables.
// Replace with live figures once the underlying analytics feeds are wired up.
const PERSONA_SNAPSHOTS = [
  {
    eyebrow: "IP Licensors & Streaming",
    icon: <IconMovie size={18} />,
    color: "indigo",
    headline: "The Next Breakout",
    stat: "68%",
    description:
      "of Shonen fans aged 18-24 are hyped for Solo Leveling S2 — 7,900+ immediate engaged viewers.",
  },
  {
    eyebrow: "Merch & Apparel Brands",
    icon: <IconShirt size={18} />,
    color: "grape",
    headline: "High-Intent Collectors",
    stat: "42%",
    description:
      "of Premium Ticketholders name 'Collectibles' as their primary interaction — top requested IP: Evangelion.",
  },
  {
    eyebrow: "Non-Endemic Sponsors",
    icon: <IconSpeakerphone size={18} />,
    color: "teal",
    headline: "Ultimate Early Adopters",
    stat: "75%",
    description:
      "of vendor-stall QR scans come from the 18-24 male demographic, averaging 6.5 min dwell time per activation.",
  },
];

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const QuickAction = ({ title, icon, color, onClick, description }) => (
  <Paper
    radius="md"
    withBorder
    p="md"
    className="cursor-pointer hover:shadow-md transition-all hover:border-blue-300 group"
    onClick={onClick}
  >
    <Group>
      <ThemeIcon
        color={color}
        variant="light"
        size={42}
        radius="md"
        className="group-hover:scale-110 transition-transform"
      >
        {icon}
      </ThemeIcon>
      <Box>
        <Text fw={700} size="sm" className="group-hover:text-blue-700">
          {title}
        </Text>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {description}
        </Text>
      </Box>
    </Group>
  </Paper>
);

export default function AdminOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({
    nickname: "Admin",
    role: "Administrator",
  });

  const [stats, setStats] = useState({
    activeProducts: 0,
    totalProducts: 0,
    verifiedUsers: 0,
    activeDeviceUsers: 0,
  });
  const [weeklyPulse, setWeeklyPulse] = useState(null);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("adminUser");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser({
          nickname:
            parsedUser.nickname || parsedUser.email?.split("@")[0] || "Admin",
          role: parsedUser.role || "ADMIN",
        });
      } catch (error) {
        console.error("Failed to load user profile", error);
      }
    }
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [prodStats, userStats, pulseStats] = await Promise.all([
          fetch(
            `${API_BASE_URL}/admin/products/stats`,
            {
              headers: getAuthHeaders(),
            },
          ).then((r) => r.json()),

          fetch(
            `${API_BASE_URL}/admin/users/stats`,
            {
              headers: getAuthHeaders(),
            },
          ).then((r) => r.json()),

          fetch(
            `${API_BASE_URL}/admin/overview/weekly-pulse`,
            {
              headers: getAuthHeaders(),
            },
          ).then((r) => r.json()),
        ]);

        // All three calls share one admin session — if the token expired
        // (24h TTL) or is missing, every one of them 401s with the same
        // shape, which used to render as silent zeros instead of an error.
        const sessionExpired = [prodStats, userStats, pulseStats].every(
          (r) => r.success === false,
        );
        setAuthError(sessionExpired);

        setStats({
          activeProducts: prodStats.data?.overview?.availableProducts || 0,
          totalProducts: prodStats.data?.overview?.totalProducts || 0,
          verifiedUsers: userStats.data?.verifiedUsers || 0,
          activeDeviceUsers: userStats.data?.activeDeviceUsers || 0,
        });
        if (pulseStats.success) setWeeklyPulse(pulseStats.data);
      } catch (error) {
        console.error("Dashboard Sync Error:", error);
        setAuthError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const userChartData = [
    {
      name: "User Base",
      Verified: stats.verifiedUsers,
      Active: stats.activeDeviceUsers,
    },
  ];

  return (
    <Box className="space-y-8 animate-fade-in">
      {authError && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="red"
          variant="light"
          radius="md"
        >
          Couldn't load dashboard data — your admin session has likely expired
          (sessions last 24 hours).{" "}
          <a href="/admin" style={{ fontWeight: 700 }}>
            Log in again
          </a>{" "}
          to refresh it.
        </Alert>
      )}

      {/* 1. HEADER */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          Dashboard Overview
        </h1>
        <Text c="dimmed">Welcome back, {user.nickname}.</Text>
      </div>

      {/* 2. QUICK ACTIONS */}
      <div>
        <Text fw={700} size="lg" mb="sm">
          Quick Actions
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="md">
          <QuickAction
            title="Create Product"
            description="Add inventory item"
            icon={<IconPackage size={22} />}
            color="blue"
            onClick={() =>
              navigate("/admin/dashboard/product-management/create")
            }
          />
          <QuickAction
            title="New Shopfront"
            description="Open a new store"
            icon={<IconBuildingStore size={22} />}
            color="grape"
            onClick={() =>
              navigate("/admin/dashboard/product-management/shopfronts")
            }
          />
          {/* <QuickAction
            title="Deep Analytics"
            description="Platform insights & funnels"
            icon={<IconChartBar size={22} />}
            color="indigo"
            onClick={() => navigate("/admin/dashboard/analytics")}
          /> */}
          <QuickAction
            title="Create Quiz"
            description="Standard engagement"
            icon={<IconBrain size={22} />}
            color="orange"
            onClick={() => navigate("/admin/dashboard/quiz-management/new")}
          />
          <QuickAction
            title="Mega Quiz"
            description="Big prize events"
            icon={<IconTicket size={22} />}
            color="red"
            onClick={() =>
              navigate("/admin/dashboard/mega-quiz-management/new")
            }
          />
          <QuickAction
            title="Broadcast"
            description="Send notifications"
            icon={<IconBell size={22} />}
            color="teal"
            onClick={() => navigate("/admin/dashboard/marketing/notifications")}
          />
        </SimpleGrid>
      </div>

      {/* 4. BUYER PERSONA B2B SNAPSHOT */}
      <Box mt="xl">
        <Group justify="space-between" mb="sm">
          <Text fw={700} size="lg">
            B2B Buyer Persona Snapshot
          </Text>
          <Badge color="dark" variant="filled">
            B2B Engine
          </Badge>
        </Group>

        {/* <Alert
          icon={<IconInfoCircle size={16} />}
          color="yellow"
          variant="light"
          radius="md"
          mb="md"
        >
          Sample data — figures below are illustrative placeholders for the
          approved buyer-persona deliverables. See{" "}
          <b>Analytics &amp; Insights</b> for the full breakdown per persona.
        </Alert> */}

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          {PERSONA_SNAPSHOTS.map((persona) => (
            <Card key={persona.headline} withBorder radius="md" p="md">
              <Group gap="xs" mb="md">
                <ThemeIcon color={persona.color} variant="light">
                  {persona.icon}
                </ThemeIcon>
                <Text fw={700} size="sm">
                  {persona.eyebrow}
                </Text>
              </Group>
              <Center h={90}>
                <Stack gap={0} align="center">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    {persona.headline}
                  </Text>
                  <Text fw={900} size="xl" className="ota-gradient-text">
                    {persona.stat}
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    {persona.description}
                  </Text>
                </Stack>
              </Center>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      <Box mt="xl">
        <Text fw={700} size="lg" mb="sm">
          Platform Pulse (Last 7 Days)
        </Text>
        <Card withBorder radius="md" p="xl">
          <Group justify="space-between" mb="md">
            <div>
              <Text fw={600}>Daily Active Users vs Engagement</Text>
              <Text size="xs" c="dimmed">
                Measures logins vs actual interactions (votes, quizzes, likes)
              </Text>
            </div>
          </Group>
          <Box h={300}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weeklyPulse || weeklyPulseData}>
                <defs>
                  <linearGradient id="colorDau" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorEng" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <RechartsTooltip />
                <Legend verticalAlign="top" height={36} />
                <Area
                  type="monotone"
                  dataKey="DAU"
                  stroke="#4F46E5"
                  fillOpacity={1}
                  fill="url(#colorDau)"
                />
                <Area
                  type="monotone"
                  dataKey="Engagement"
                  stroke="#10B981"
                  fillOpacity={1}
                  fill="url(#colorEng)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      </Box>

      {/* 3. VISUALIZATIONS & METRICS */}
      <Grid gutter="lg">
        {/* LEFT COL: User Visual Representation */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card withBorder radius="md" p="xl" className="h-full">
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={700} size="lg">
                  User Engagement
                </Text>
                <Text size="xs" c="dimmed">
                  Verified Accounts vs Active Devices
                </Text>
              </div>
              <ThemeIcon variant="light" color="indigo" size="lg">
                <IconUsers size={20} />
              </ThemeIcon>
            </Group>

            <Grid align="center">
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <Box h={250}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={userChartData}
                      layout="vertical"
                      barSize={40}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" hide />
                      <RechartsTooltip cursor={{ fill: "transparent" }} />
                      <Legend
                        verticalAlign="top"
                        align="left"
                        iconType="circle"
                      />
                      <Bar
                        dataKey="Verified"
                        name="Verified Emails"
                        fill="#4F46E5"
                        radius={[0, 4, 4, 0]}
                        label={{ position: "right", fill: "#666" }}
                      />
                      <Bar
                        dataKey="Active"
                        name="Active on Device"
                        fill="#10B981"
                        radius={[0, 4, 4, 0]}
                        label={{ position: "right", fill: "#666" }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid.Col>

              {/* Legend/Summary Side */}
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Stack gap="md">
                  <Paper
                    withBorder
                    p="xs"
                    radius="md"
                    className="border-l-4 border-l-indigo-600"
                  >
                    <Group>
                      <ThemeIcon color="indigo" variant="light" radius="xl">
                        <IconCheck size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                          Verified Users
                        </Text>
                        <Text fw={700} size="lg">
                          {stats.verifiedUsers}
                        </Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper
                    withBorder
                    p="xs"
                    radius="md"
                    className="border-l-4 border-l-emerald-500"
                  >
                    <Group>
                      <ThemeIcon color="green" variant="light" radius="xl">
                        <IconDeviceMobile size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                          Active Devices
                        </Text>
                        <Text fw={700} size="lg">
                          {stats.activeDeviceUsers}
                        </Text>
                      </div>
                    </Group>
                  </Paper>
                </Stack>
              </Grid.Col>
            </Grid>
          </Card>
        </Grid.Col>

        {/* RIGHT COL: Active Products KPI */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card
            withBorder
            radius="md"
            p="xl"
            className="h-full bg-blue-50 border-blue-100"
          >
            <Stack justify="space-between" h="100%">
              <div>
                <Group justify="space-between" align="start">
                  <Text fw={700} size="lg" c="blue.9">
                    Inventory Status
                  </Text>
                  <ThemeIcon color="blue" variant="white" size="xl" radius="md">
                    <IconPackage size={28} />
                  </ThemeIcon>
                </Group>

                <Center py="xl">
                  <RingProgress
                    size={180}
                    thickness={16}
                    roundCaps
                    sections={[
                      {
                        value:
                          (stats.activeProducts / (stats.totalProducts || 1)) *
                          100,
                        color: "blue",
                      },
                    ]}
                    label={
                      <Center>
                        <Stack gap={0} align="center">
                          <Text fw={900} size="3rem" lh={1} c="blue">
                            {stats.activeProducts}
                          </Text>
                          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                            Active Items
                          </Text>
                        </Stack>
                      </Center>
                    }
                  />
                </Center>
              </div>

              <Button
                variant="white"
                color="blue"
                fullWidth
                rightSection={<IconArrowRight size={16} />}
                onClick={() => navigate("/admin/dashboard/product-management")}
              >
                Manage Inventory
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </Box>
  );
}
